import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import api from '../api';
import useGet from '../hooks/useGet';
import { useTranslation } from 'react-i18next';

const Hero = () => {
    const { t } = useTranslation();

    const tr = useCallback(
        (key) => {
            return t(`hero:${key}`);
        },
        [t]
    );

    const [currentIndex, setCurrentIndex] = useState(0);
    const { data, isLoading } = useGet('timeline', api.timeline.getTimeline, 'Failed to fetch timeline', (data) => data?.data);

    const displayData = data || [];

    if (!displayData || displayData.length === 0) {
        return (
            <div className="container mx-auto px-4 py-12">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-700">{tr('noData')}</h2>
                    <p className="text-gray-500 mt-2">{tr('noDataDescription')}</p>
                </div>
            </div>
        );
    }

    const nextSlide = () => {
        setCurrentIndex((current) => (current === displayData.length - 1 ? 0 : current + 1));
    };

    const prevSlide = () => {
        setCurrentIndex((current) => (current === 0 ? displayData.length - 1 : current - 1));
    };

    const goToSlide = (index) => {
        setCurrentIndex(index);
    };

    const MediaContent = ({ item }) => {
        const [mediaLoaded, setMediaLoaded] = useState(false);
        const [isVideo, setIsVideo] = useState(false);
        const [hasError, setHasError] = useState(false);

        useEffect(() => {
            if (item) {
                setIsVideo(item?.type === 'video');
            }
        }, [item]);

        if (!item) {
            return (
                <div className="relative aspect-video rounded-lg overflow-hidden shadow-lg bg-gray-100 flex items-center justify-center">
                    <p className="text-gray-500">No media content available</p>
                </div>
            );
        }

        const handleMediaLoad = () => {
            setMediaLoaded(true);
            setHasError(false);
        };

        const handleMediaError = () => {
            setMediaLoaded(true);
            setHasError(true);
        };

        const getYouTubeVideoId = (url) => {
            const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
            const match = url.match(regExp);
            return match && match[2].length === 11 ? match[2] : null;
        };

        const getMediaContent = () => {
            const thumbnail = item?.thumbnail?.uri;
            const url = item?.url;
            const type = item?.media_type?.name?.toLowerCase();

            if (!url && thumbnail) {
                return <img src={`data:image/jpeg;base64,${thumbnail}`} alt={item?.heading} className={`object-cover w-full h-full transition-transform duration-700 hover:scale-110 ${!mediaLoaded ? 'hidden' : ''}`} onLoad={handleMediaLoad} onError={handleMediaError} />;
            }

            const isValidUrl = (url) => {
                try {
                    new URL(url);
                    return true;
                } catch (error) {
                    return false;
                }
            };

            switch (type) {
                case 'video':
                    if (url?.includes('youtube.com') || url?.includes('youtu.be')) {
                        const videoId = getYouTubeVideoId(url);
                        return (
                            <div className="relative w-full h-full">
                                <iframe
                                    className={`absolute top-0 left-0 w-full h-full ${!mediaLoaded ? 'hidden' : ''}`}
                                    src={`https://www.youtube.com/embed/${videoId}`}
                                    title={item?.heading}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    onLoad={handleMediaLoad}
                                />
                            </div>
                        );
                    }
                    return isValidUrl(url) ? (
                        <img src={url} alt={item?.heading} className={`object-cover w-full h-full transition-transform duration-700 hover:scale-110 ${!mediaLoaded ? 'hidden' : ''}`} onLoad={handleMediaLoad} />
                    ) : thumbnail ? (
                        <img src={`data:image/jpeg;base64,${thumbnail}`} alt={item?.heading} className="object-cover w-full h-full" onLoad={handleMediaLoad} />
                    ) : (
                        <div className="flex items-center justify-center w-full h-full bg-gray-200 text-gray-500">
                            <i className="fas fa-image text-4xl"></i>
                        </div>
                    );
                case 'pdf':
                    return <iframe className={`w-full h-full ${!mediaLoaded ? 'hidden' : ''}`} onLoad={handleMediaLoad} src={url} title={item.heading} />;
                case 'image':
                default:

                    return isValidUrl(url) ? (
                        <img src={url} alt={item?.heading} className={`object-cover w-full h-full transition-transform duration-700 hover:scale-110 ${!mediaLoaded ? 'hidden' : ''}`} onLoad={handleMediaLoad} onError={handleMediaError} />
                    ) : thumbnail ? (
                        <img src={`data:image/jpeg;base64,${thumbnail}`} alt={item?.heading} className="object-cover w-full h-full" onLoad={handleMediaLoad} onError={handleMediaError} />
                    ) : (
                        <div className="flex items-center justify-center w-full h-full bg-gray-200 text-gray-500">
                            <i className="fas fa-image text-4xl"></i>
                        </div>
                    );
            }
        };

        return (
            <div className="relative aspect-video rounded-lg overflow-hidden shadow-lg">
                {!mediaLoaded && (
                    <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
                        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                )}

                {getMediaContent()}

                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-6 transform transition-all duration-300">
                    <h3 className="text-white text-2xl font-bold mb-1">{item.heading || 'Untitled'}</h3>
                    <p className="text-white/90 text-lg">{new Date(item.date).getFullYear() || '-'}</p>
                </div>
            </div>
        );
    };

    return (
        <div className="w-full mt-10 bg-gradient-to-br from-white via-blue-50 to-gray-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="w-full transform transition-all p-6 duration-500 hover:scale-[1.02]">
                    <MediaContent item={displayData[currentIndex]} />
                </div>
                <div className="flex flex-col justify-center space-y-4 p-4 transform transition-all duration-500 ease-in-out translate-x-0 opacity-100 animate-fadeIn" key={currentIndex}>
                    <h2 className="text-3xl font-bold text-[#50A1CA] ml-4">{displayData[currentIndex]?.heading || 'Untitled'}</h2>
                    <div className="flex flex-col space-y-4 bg-gray-100 p-4 rounded-lg dark:bg-gray-700">
                        <div className="flex items-center">
                            <span className="text-xl font-semibold text-[#50A1CA]">{new Date(displayData[currentIndex]?.date).getFullYear() || '-'}</span>
                        </div>
                        <p className="text-gray-500 dark:text-gray-300 text-lg leading-relaxed">{displayData[currentIndex]?.event || 'No description available'}</p>
                    </div>
                </div>
            </div>

            <div className="w-full flex justify-between items-center px-4 mb-12">
                <button onClick={prevSlide} className="p-2 rounded-full bg-gray-300 hover:bg-blue-100 transition-all duration-300 transform hover:scale-110 hover:shadow-md">
                    <FontAwesomeIcon icon={faChevronLeft} className="text-gray-600 hover:text-blue-600" />
                </button>

                <div className="flex-1 mx-6">
                    <div className="relative h-3 bg-gray-200 dark:bg-gray-700 rounded-full shadow-inner">
                        <div className="absolute top-0 left-0 right-0 flex justify-between px-1 -mt-4">
                            {displayData.map((item, index) => (
                                <button
                                    key={index}
                                    onClick={() => goToSlide(index)}
                                    className={`w-.5 h-3 rounded-full transition-all duration-300 transform
                                            ${index === currentIndex ? 'bg-[#50A1CA] ring-1 ring-blue-200 scale-125 shadow-lg' : 'bg-gray-400 hover:bg-blue-400 hover:scale-110 hover:shadow-md'}`}
                                >
                                    <span
                                        className={`absolute top-8 left-1/2 transform -translate-x-1/2 text-xs font-medium
                                                whitespace-nowrap px-2 py-1 rounded-full transition-all duration-300
                                                ${index === currentIndex ? 'bg-[#50A1CA]/10 text-[#50A1CA] border border-[#50A1CA] scale-110' : 'bg-gray-300 text-gray-600 border border-gray-600'}`}
                                    >
                                        {new Date(item?.date).getFullYear() || '-'}
                                    </span>
                                </button>
                            ))}
                        </div>

                        <div
                            className="absolute h-3 bg-gradient-to-r from-[#50A1CA] to-[#50A1CA]/10 rounded-full transition-all duration-500 ease-in-out"
                            style={{
                                width: `${(currentIndex / (displayData.length - 1)) * 100}%`,
                                boxShadow: '0 2px 10px rgba(59, 130, 246, 0.5)'
                            }}
                        />
                    </div>
                </div>

                <button onClick={nextSlide} className="p-2 rounded-full bg-gray-300 hover:bg-blue-100 transition-all duration-300 transform hover:scale-110 hover:shadow-md">
                    <FontAwesomeIcon icon={faChevronRight} className="text-gray-600 hover:text-blue-600" />
                </button>
            </div>
        </div>
    );
};

export default Hero;
